import * as d3 from "d3";

import { DatasetRecord, LineChartDataset } from "./chart/LineChart";

export interface Bounds2D {
  height: number;
  width: number;
  x: number;
  y: number;
}

const EmptyBounds2D: Bounds2D = {
  height: 0,
  width: 0,
  x: 0,
  y: 0
}

interface BuildChartAreaProps {
  fontSize: number;
  height: number;
  margin: {
    bottom: number;
    left: number
    right: number;
    top: number;
  }
  showXAxis: boolean;
  showYAxis: boolean;
  tickLength: number;
  width: number;
}

export function buildChartAreas({ fontSize, height, margin, tickLength, width, showYAxis, showXAxis }: BuildChartAreaProps)
  : { lineArea: Bounds2D, xAxis: Bounds2D, yAxis: Bounds2D } {
  const lineArea: Bounds2D = {
    height: height - margin.top - margin.bottom,
    width: width - margin.left - margin.right,
    x: margin.left,
    y: margin.top
  };
  const xAxis = {...EmptyBounds2D};
  const yAxis = {...EmptyBounds2D};

  if (showYAxis) {
    yAxis.x = margin.left;
    yAxis.y = margin.top;
    yAxis.height = height - margin.top - margin.bottom;
    yAxis.width = 6 * (fontSize * 0.67) + tickLength;

    lineArea.width -= yAxis.width;
    lineArea.x += yAxis.width;
  }

  if (showXAxis) {
    xAxis.height = fontSize + tickLength;
    xAxis.width = width - yAxis.width - margin.left - margin.right;
    xAxis.x = yAxis.width + margin.left;
    xAxis.y = height - xAxis.height - margin.bottom;

    yAxis.height -= xAxis.height;

    lineArea.height -= xAxis.height;
  }

  return { lineArea, xAxis, yAxis };
}

export function getValue(record: DatasetRecord, records: Array<DatasetRecord>, props: {
    dataKey: string,
    valueFormatter?: (value: any) => number,
    valueGetter?: (value: any, row: DatasetRecord, array: Array<DatasetRecord>) => any
  }): number {
  let value = record[props.dataKey];
  if (props.valueGetter) {
    value = props.valueGetter(value, record, records);
  }
  if (props.valueFormatter) {
    value = props.valueFormatter(value);
  }
  return value;
}

export function extent(datasets: Array<LineChartDataset>, accessor: (record: DatasetRecord) => number): [number, number] {
  let min = 0;
  let max = 0;
  for (let i=0; i<datasets.length; i++) {
    const series = datasets[i].records;
    const [seriesMin = 0, seriesMax = 0] = d3.extent(series, (d) => accessor(d) ?? 0);
    if (i === 0) {
      min = seriesMin;
      max = seriesMax;
      continue;
    }
    if (seriesMin < min) {
      min = seriesMin;
    }
    if (seriesMax > max) {
      max = seriesMax;
    }
  }
  return [min, max];
}

export function alignDatasetsStart(datasets: Array<LineChartDataset>): void {
  let min = 0;
  // Find the minimum start value
  for (let i=0; i<datasets.length; i++) {
    const series = datasets[i].records;
    const firstValue = series[0]._x ?? 0;
    if (i === 0 || firstValue < min) {
      min = firstValue;
    }
  }
  // Adjust all values to line up with the start value
  for (const dataset of datasets) {
    const firstValue = dataset.records[0]._x ?? 0;
    if (firstValue === min) {
      continue;
    }
    const difference = firstValue - min;
    for (let i=0; i<dataset.records.length; i++) {
      const data = dataset.records[i];
      data._x = (data._x ?? 0) - difference;
    }
  }
}